/**
 * SetProfile Routing Validation Panel (2026-05-13)
 *
 * Analytics-only dashboard answering:
 *   1. Which market family is actually profitable?
 *   2. Which archetypes work with which markets?
 *   3. Is shadow smarter than legacy?
 *   4. Does governance improve routing quality?
 *   5. Does final_score correlate with real ROI?
 *   6. Are structural O/U filters useful?
 *   7. Which combinations should be promoted / scout / rejected?
 *
 * Renders 8 sections (Priorities 1..8) below the existing shadow panel.
 * Metrics requiring book-odds persistence (ROI%, PnL, avg_odds, CLV,
 * drawdown) are shown as 'N/A' with reason from data_availability.
 *
 * No production behavior change. Audit/UI only.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { adminGet } from '../data/api';
import { Activity, RefreshCcw, Info } from 'lucide-react';

const fmtInt = (v) => (v == null ? '—' : Number(v).toLocaleString('it-IT'));
const fmtPct = (v) => (v == null ? '—' : `${Number(v).toFixed(1)}%`);
const fmtN = (v, d = 3) => (v == null ? '—' : Number(v).toFixed(d));

const wrColor = (wr) => {
  if (wr == null) return 'text-zinc-500';
  if (wr >= 60) return 'text-emerald-300';
  if (wr >= 50) return 'text-emerald-200';
  if (wr >= 40) return 'text-amber-200';
  return 'text-rose-300';
};

const sampleBadge = (tier) => {
  const map = {
    LOW: { color: 'bg-rose-950/60 border-rose-800 text-rose-300', label: 'LOW' },
    MEDIUM: { color: 'bg-yellow-950/60 border-yellow-800 text-yellow-300', label: 'MED' },
    USABLE: { color: 'bg-teal-950/60 border-teal-800 text-teal-300', label: 'USABLE' },
    STRONG: { color: 'bg-emerald-950/60 border-emerald-800 text-emerald-300', label: 'STRONG' },
  };
  const t = map[tier] || map.LOW;
  return (
    <span data-testid="sample-tier-badge"
          className={`inline-block px-1 py-0.5 text-[8px] rounded-sm border ${t.color}`}>
      {t.label}
    </span>
  );
};

function Section({ title, testid, children, hint }) {
  return (
    <div data-testid={testid} className="rounded-sm border border-zinc-800 bg-zinc-950/40 p-2 space-y-1.5">
      <div className="flex items-center justify-between">
        <div className="text-[10px] text-zinc-300 tracking-wider font-semibold uppercase">{title}</div>
        {hint && (
          <span className="text-[9px] text-zinc-500 italic" title={hint}>
            <Info className="inline w-3 h-3 mr-0.5" /> ?
          </span>
        )}
      </div>
      {children}
    </div>
  );
}

const COLS_BASIC = [
  { k: 'key', label: 'key', cls: 'text-left' },
  { k: 'n', label: 'n', fmt: fmtInt },
  { k: 'settled', label: 'settled', fmt: fmtInt },
  { k: 'wins', label: 'W', fmt: fmtInt },
  { k: 'losses', label: 'L', fmt: fmtInt },
  { k: 'wr_pct', label: 'WR%', fmt: fmtPct, color: wrColor },
  { k: 'avg_final_score', label: 'avg_final', fmt: (v) => fmtN(v, 3) },
  { k: 'avg_structural_score', label: 'avg_struct', fmt: (v) => fmtN(v, 3) },
  { k: 'avg_ev_cons_pct', label: 'avg_EV%', fmt: (v) => fmtN(v, 2) },
];

const COLS_OU_DETAIL = [
  { k: 'key', label: 'verdict', cls: 'text-left' },
  { k: 'n', label: 'n', fmt: fmtInt },
  { k: 'wins', label: 'W', fmt: fmtInt },
  { k: 'losses', label: 'L', fmt: fmtInt },
  { k: 'wr_pct', label: 'WR%', fmt: fmtPct, color: wrColor },
  { k: 'avg_selected_line', label: 'avg_line', fmt: (v) => fmtN(v, 1) },
  { k: 'avg_expected_total_minus_line', label: 'Δline', fmt: (v) => fmtN(v, 2) },
  { k: 'avg_density_edge', label: 'density', fmt: (v) => fmtN(v, 3) },
  { k: 'avg_expected_sets', label: 'E[sets]', fmt: (v) => fmtN(v, 2) },
  { k: 'avg_over_probability', label: 'p(over)', fmt: (v) => fmtN(v, 3) },
];

const COLS_HC_DETAIL = [
  { k: 'key', label: 'detail', cls: 'text-left' },
  { k: 'n', label: 'n', fmt: fmtInt },
  { k: 'wins', label: 'W', fmt: fmtInt },
  { k: 'losses', label: 'L', fmt: fmtInt },
  { k: 'wr_pct', label: 'WR%', fmt: fmtPct, color: wrColor },
  { k: 'avg_favorite_quality', label: 'avg_FQ', fmt: (v) => fmtN(v, 3) },
  { k: 'avg_alignment_gap', label: 'avg_disp', fmt: (v) => fmtN(v, 3) },
];

const COLS_PAIR = [
  { k: 'a', label: 'a', cls: 'text-left' },
  { k: 'b', label: 'b', cls: 'text-left' },
  { k: 'n', label: 'n', fmt: fmtInt },
  { k: 'settled', label: 'sett', fmt: fmtInt },
  { k: 'wins', label: 'W', fmt: fmtInt },
  { k: 'losses', label: 'L', fmt: fmtInt },
  { k: 'wr_pct', label: 'WR%', fmt: fmtPct, color: wrColor },
  { k: 'avg_final_score', label: 'avg_final', fmt: (v) => fmtN(v, 3) },
];

function AggTable({ rows, columns, testid, emptyText = 'no data' }) {
  if (!rows || rows.length === 0) {
    return <div className="text-[10px] text-zinc-600 py-1.5" data-testid={`${testid}-empty`}>{emptyText}</div>;
  }
  return (
    <div className="overflow-x-auto">
      <table data-testid={testid} className="w-full text-[10px]" style={{ fontFamily: 'monospace' }}>
        <thead>
          <tr className="text-zinc-500 border-b border-zinc-800">
            {columns.map((c) => (
              <th key={c.k} className={`py-1 px-1 ${c.cls || 'text-right'}`}>{c.label}</th>
            ))}
            <th className="py-1 px-1 text-right">sample</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={i} className="border-b border-zinc-900/60 hover:bg-zinc-900/40">
              {columns.map((c) => {
                const v = r[c.k];
                const formatted = c.fmt ? c.fmt(v) : (v == null ? '—' : String(v));
                const cls = c.color ? c.color(v) : 'text-zinc-300';
                return (
                  <td key={c.k} className={`py-1 px-1 ${c.cls || 'text-right'} ${cls}`}>
                    {formatted}
                  </td>
                );
              })}
              <td className="py-1 px-1 text-right">{sampleBadge(r.sample_tier)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

export default function SetProfileRoutingValidationPanel({ adminPin }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [window, setWindow] = useState('7d');
  const [familyFilter, setFamilyFilter] = useState('');
  const [profileFilter, setProfileFilter] = useState('');
  const [modeFilter, setModeFilter] = useState('');

  const fetchData = async () => {
    setLoading(true);
    setErr(null);
    try {
      const qs = new URLSearchParams();
      qs.set('window', window);
      if (familyFilter) qs.set('market_family_filter', familyFilter);
      if (profileFilter) qs.set('profile_type_filter', profileFilter);
      if (modeFilter) qs.set('mode', modeFilter);
      const r = await adminGet(
        `/tt/admin/setprofile-routing-validation?${qs.toString()}`,
        adminPin,
      );
      setData(r);
    } catch (e) {
      setErr(String(e?.message || e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [window, familyFilter, profileFilter, modeFilter]);

  const agg = data?.aggregations || {};
  const unavail = data?.data_availability?.unavailable || [];

  return (
    <div data-testid="routing-validation-panel" className="space-y-3 rounded-sm border border-zinc-800 bg-zinc-950/50 p-3">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-2">
          <Activity className="w-4 h-4 text-cyan-400" />
          <span className="text-xs font-semibold text-zinc-100 uppercase tracking-wider">
            Routing Validation
          </span>
          <span className="text-[9px] text-zinc-500 italic">analytics only · no production change</span>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap text-[10px]">
          {['24h', '3d', '7d', '30d', 'all'].map((w) => (
            <button key={w}
                    data-testid={`rv-window-${w}`}
                    onClick={() => setWindow(w)}
                    className={`px-2 py-0.5 rounded-sm border ${window === w
                      ? 'border-cyan-500 bg-cyan-950/40 text-cyan-200'
                      : 'border-zinc-700 text-zinc-400 hover:border-zinc-500'}`}>
              {w}
            </button>
          ))}
          <select data-testid="rv-family-filter"
                  value={familyFilter} onChange={(e) => setFamilyFilter(e.target.value)}
                  className="text-[10px] px-1.5 py-0.5 rounded-sm bg-zinc-900 border border-zinc-700 text-zinc-300">
            <option value="">all markets</option>
            <option value="MW">MW</option>
            <option value="HC">HC</option>
            <option value="O/U">O/U</option>
            <option value="NONE">NONE</option>
          </select>
          <select data-testid="rv-mode-filter"
                  value={modeFilter} onChange={(e) => setModeFilter(e.target.value)}
                  className="text-[10px] px-1.5 py-0.5 rounded-sm bg-zinc-900 border border-zinc-700 text-zinc-300">
            <option value="">scout+live</option>
            <option value="scout">scout only</option>
            <option value="live">live only</option>
          </select>
          <button data-testid="rv-refresh-btn"
                  onClick={fetchData}
                  className="px-2 py-0.5 rounded-sm border border-zinc-700 hover:border-zinc-500 text-zinc-300">
            <RefreshCcw className={`inline w-3 h-3 ${loading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {err && (
        <div data-testid="rv-error" className="text-[10px] text-rose-400 bg-rose-950/30 border border-rose-900 rounded-sm p-1.5">
          {err}
        </div>
      )}

      <div className="text-[9px] text-zinc-500 flex flex-wrap gap-2">
        <span>n_analyses: <span className="text-zinc-300">{fmtInt(data?.n_analyses)}</span></span>
        <span>n_settled: <span className="text-zinc-300">{fmtInt(data?.n_settled)}</span></span>
        <span>window: <span className="text-zinc-300">{data?.window}</span></span>
        {unavail.length > 0 && (
          <span title={unavail.map(u => `${u.field}: ${u.reason}`).join('\n')}
                className="text-amber-400/70 cursor-help">
            ⚠ {unavail.length} metrics unavailable (ROI / PnL / CLV — see tooltip)
          </span>
        )}
      </div>

      <Section title="① Market Family Performance" testid="rv-sec-p1"
                hint="Which market family actually performs best">
        <AggTable rows={agg.by_market_family} columns={COLS_BASIC} testid="rv-tbl-family" />
      </Section>

      <Section title="② Market Detail Performance" testid="rv-sec-p2"
                hint="Separates MW_P1/P2, HC_FAV/DOG, OVER/UNDER">
        <AggTable rows={agg.by_market_detail} columns={COLS_BASIC} testid="rv-tbl-detail" />
      </Section>

      <Section title="③ Profile Type × Market Family" testid="rv-sec-p3"
                hint="Validates archetype→market routing philosophy">
        <AggTable rows={agg.by_profile_x_market_family} columns={COLS_PAIR} testid="rv-tbl-profile-x-family" />
      </Section>

      <Section title="④ Legacy vs Shadow Routing (detailed)" testid="rv-sec-p4"
                hint="Confronto profondo legacy → shadow per ogni picking choice">
        <AggTable rows={agg.by_legacy_x_shadow_routing} columns={COLS_PAIR} testid="rv-tbl-legacy-x-shadow" />
      </Section>

      <Section title="⑤ Governance × Market" testid="rv-sec-p5"
                hint="Validates whether governance dimensions improve quality">
        <div className="space-y-2">
          <div>
            <div className="text-[9px] text-zinc-500 mb-0.5">by alignment_tier × market</div>
            <AggTable rows={agg.by_governance?.by_alignment_tier} columns={COLS_PAIR} testid="rv-tbl-gov-align" />
          </div>
          <div>
            <div className="text-[9px] text-zinc-500 mb-0.5">by favorite_quality × market</div>
            <AggTable rows={agg.by_governance?.by_favorite_quality_tier} columns={COLS_PAIR} testid="rv-tbl-gov-fq" />
          </div>
          <div>
            <div className="text-[9px] text-zinc-500 mb-0.5">by structural_ou_status × market</div>
            <AggTable rows={agg.by_governance?.by_structural_ou_status} columns={COLS_PAIR} testid="rv-tbl-gov-so" />
          </div>
          <div>
            <div className="text-[9px] text-zinc-500 mb-0.5">by split_brain × market</div>
            <AggTable rows={agg.by_governance?.by_split_brain} columns={COLS_PAIR} testid="rv-tbl-gov-sb" />
          </div>
          <div>
            <div className="text-[9px] text-zinc-500 mb-0.5">by scout/live × market</div>
            <AggTable rows={agg.by_governance?.by_scout_vs_live} columns={COLS_PAIR} testid="rv-tbl-gov-scout" />
          </div>
        </div>
      </Section>

      <Section title="⑥ Final Score Buckets" testid="rv-sec-p6"
                hint="Final_score correlation with outcome (predictive validation)">
        <AggTable rows={agg.by_final_score_bucket} columns={COLS_BASIC} testid="rv-tbl-final-score" />
      </Section>

      <Section title="⑦ Structural O/U Audit" testid="rv-sec-p7"
                hint="Does density/expected_sets/blowout actually improve O/U?">
        <AggTable rows={agg.by_structural_ou_audit} columns={COLS_OU_DETAIL} testid="rv-tbl-ou-audit" />
      </Section>

      <Section title="⑧ HC Validation" testid="rv-sec-p8"
                hint="Aggressive favorite HC stability check">
        <AggTable rows={agg.by_hc_detail} columns={COLS_HC_DETAIL} testid="rv-tbl-hc-detail" />
      </Section>

      <Section title="⑨ True Long Score Buckets (O/U)" testid="rv-sec-tls"
                hint="Validates whether the TLS audit metric tracks real Over hit-rate. Fake-long warning count shown below.">
        <AggTable rows={agg.by_true_long_score_bucket} columns={COLS_BASIC} testid="rv-tbl-tls" />
        <div className="text-[9px] text-amber-400/80 mt-1" data-testid="rv-fake-long-count">
          ⚠ fake_long_risk hits: {data?.fake_long_risk_count ?? 0}
        </div>
      </Section>

      <div className="text-[9px] text-zinc-600 italic border-t border-zinc-900 pt-1.5">
        Future tracking note: to unlock ROI / PnL / avg_odds / CLV / drawdown the
        shadow recommendations must persist {' '}
        <span className="text-zinc-400">odds_at_selection</span>,{' '}
        <span className="text-zinc-400">selected_market_side</span>,{' '}
        <span className="text-zinc-400">implied_probability</span>,{' '}
        <span className="text-zinc-400">ev_raw</span>,{' '}
        <span className="text-zinc-400">closing_odds</span>,{' '}
        <span className="text-zinc-400">clv_pct</span>.{' '}
        Audit-only until then.
      </div>
    </div>
  );
}
